// components/VoyageInfoTable.js
import React, { memo } from "react";
import {
  Paper,
  Typography,
  Box,
  Button,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  Print as PrintIcon,
  DirectionsBoat as VesselIcon,
  EventNote as DateIcon,
  Place as PortIcon,
  Anchor as JettyIcon,
  Assignment as VoyageIcon,
  SettingsSuggest as OpIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { getVoyageNumber } from "../../utils/domainSelectors";

const InfoItem = ({ icon: Icon, label, value }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 40,
          height: 40,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          color: "primary.main",
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 600,
            display: "block",
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {label}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 700 }}>
          {value || "N/A"}
        </Typography>
      </Box>
    </Box>
  );
};

const VoyageInfoTable = ({
  currentVoyage,
  selectedTrip,
  onPrint,
}) => {
  const { t } = useTranslation("statementoffact");
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        my: 3,
        borderRadius: 4,
        border: "1px solid",
        borderColor: "divider",
        background: `linear-gradient(135deg, ${
          theme.palette.background.paper
        } 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={3}
      >
        <Box>
          <Typography
            variant="h6"
            fontWeight="900"
            sx={{ color: "text.primary", mb: 0.5 }}
          >
            {t("voyageInfo.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Quick overview of the current operation and vessel details.
          </Typography>
        </Box>

        <Button
          variant="contained"
          disableElevation
          startIcon={<PrintIcon />}
          onClick={onPrint}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 800,
            textTransform: "none",
            boxShadow: `0 4px 14px 0 ${alpha(theme.palette.primary.main, 0.3)}`,
          }}
        >
          {t("voyageInfo.printButton")}
        </Button>
      </Box>

      <Divider sx={{ mb: 3, opacity: 0.6 }} />

      <Grid container spacing={4}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <InfoItem
            icon={VesselIcon}
            label={t("voyageInfo.vessel")}
            value={currentVoyage?.vessel}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <InfoItem
            icon={DateIcon}
            label={t("voyageInfo.date")}
            value={selectedTrip?.date}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <InfoItem
            icon={VoyageIcon}
            label={t("voyageInfo.voyage")}
            value={getVoyageNumber(currentVoyage)}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <InfoItem
            icon={PortIcon}
            label={t("voyageInfo.port")}
            value={selectedTrip?.to}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <InfoItem
            icon={JettyIcon}
            label={t("voyageInfo.jetty")}
            value={selectedTrip?.jetty}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <InfoItem
            icon={OpIcon}
            label={t("voyageInfo.operation")}
            value={selectedTrip?.operationType}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default memo(VoyageInfoTable);
